import ImageWithSkeleton from '@/components/base/ImageWithSkeleton';

export const ROOM_LIST = [
  { id: 'living-room', label: 'Living Room', icon: 'ri-sofa-line', color: 'text-sky-500', bg: 'bg-sky-500/10' },
  { id: 'master-bedroom', label: 'Master Bedroom', icon: 'ri-hotel-bed-line', color: 'text-violet-500', bg: 'bg-violet-500/10' },
  { id: 'kitchen', label: 'Kitchen', icon: 'ri-restaurant-line', color: 'text-amber-500', bg: 'bg-amber-500/10' },
  { id: 'dining-room', label: 'Dining Room', icon: 'ri-goblet-line', color: 'text-orange-500', bg: 'bg-orange-500/10' },
  { id: 'bathroom', label: 'Bathroom', icon: 'ri-water-flash-line', color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
  { id: 'home-office', label: 'Home Office', icon: 'ri-computer-line', color: 'text-teal-500', bg: 'bg-teal-500/10' },
  { id: 'foyer', label: 'Foyer', icon: 'ri-door-open-line', color: 'text-green-500', bg: 'bg-green-500/10' },
  { id: 'gym', label: 'Gym', icon: 'ri-run-line', color: 'text-rose-500', bg: 'bg-rose-500/10' },
];

interface FavoriteRoomsPanelProps {
  darkMode: boolean;
  favoriteRooms: string[];
  onToggleFavorite: (roomId: string) => void;
  onViewRoom: (roomId: string, roomLabel: string) => void;
  onCompareRooms: (roomA: string, roomB: string) => void;
  selectedStyle: string;
  getRoomInteriorImage: (label: string, style: string) => string;
}

export default function FavoriteRoomsPanel({
  darkMode,
  favoriteRooms,
  onToggleFavorite,
  onViewRoom,
  onCompareRooms,
  selectedStyle,
  getRoomInteriorImage,
}: FavoriteRoomsPanelProps) {
  const cardBg = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const subtext = darkMode ? 'text-gray-400' : 'text-gray-500';

  const favoriteRoomObjects = ROOM_LIST.filter((r) => favoriteRooms.includes(r.id));

  return (
    <div className={`rounded-2xl border shadow-lg overflow-hidden ${cardBg}`} style={{ width: 320 }}>
      {/* Header */}
      <div className={`flex items-center gap-3 px-4 py-3 border-b ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-rose-500 to-pink-500">
          <i className="ri-star-fill text-white text-sm"></i>
        </div>
        <div className="flex-1">
          <p className={`text-sm font-bold ${text}`}>Favorite Rooms</p>
          <p className={`text-xs ${subtext}`}>{favoriteRooms.length} starred</p>
        </div>
        {favoriteRooms.length >= 2 && (
          <button
            onClick={() => onCompareRooms(favoriteRooms[0], favoriteRooms[1])}
            className="px-2.5 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            <i className="ri-layout-column-line text-xs"></i>Compare
          </button>
        )}
      </div>

      {/* All rooms list with star toggle */}
      <div className="p-3 space-y-1.5 max-h-72 overflow-y-auto">
        {ROOM_LIST.map((room) => {
          const isFav = favoriteRooms.includes(room.id);
          return (
            <div
              key={room.id}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all ${
                isFav
                  ? darkMode ? 'bg-rose-900/20 border border-rose-800/40' : 'bg-rose-50 border border-rose-100'
                  : darkMode ? 'hover:bg-gray-700/50 border border-transparent' : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${room.bg}`}>
                <i className={`${room.icon} text-sm ${room.color}`}></i>
              </div>
              <span className={`flex-1 text-sm font-semibold ${text}`}>{room.label}</span>
              <div className="flex items-center gap-1.5">
                {isFav && (
                  <button
                    onClick={() => onViewRoom(room.id, room.label)}
                    className={`w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
                      darkMode ? 'bg-gray-700 hover:bg-teal-700 text-gray-300 hover:text-white' : 'bg-gray-100 hover:bg-teal-500 text-gray-600 hover:text-white'
                    }`}
                    title="View interior"
                  >
                    <i className="ri-eye-line text-xs"></i>
                  </button>
                )}
                <button
                  onClick={() => onToggleFavorite(room.id)}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-all ${
                    isFav
                      ? 'bg-rose-500 text-white hover:bg-rose-600'
                      : darkMode ? 'bg-gray-700 text-gray-400 hover:bg-rose-900/40 hover:text-rose-400' : 'bg-gray-100 text-gray-400 hover:bg-rose-50 hover:text-rose-500'
                  }`}
                  title={isFav ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <i className={`${isFav ? 'ri-star-fill' : 'ri-star-line'} text-xs`}></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Favorited rooms preview strip */}
      {favoriteRoomObjects.length > 0 && (
        <div className={`border-t px-3 py-3 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className={`text-xs font-semibold mb-2 ${subtext}`}>Starred Rooms Preview</p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {favoriteRoomObjects.map((room) => (
              <button
                key={room.id}
                onClick={() => onViewRoom(room.id, room.label)}
                className="relative flex-shrink-0 w-20 h-16 rounded-xl overflow-hidden border-2 border-rose-400/60 cursor-pointer group hover:border-rose-500 transition-all"
              >
                <ImageWithSkeleton
                  src={getRoomInteriorImage(room.label, selectedStyle)}
                  alt={room.label}
                  className="w-full h-full object-cover object-top"
                  darkMode={darkMode}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <i className="ri-eye-line text-white text-sm"></i>
                </div>
                <p className="absolute bottom-1 left-0 right-0 text-center text-white text-[9px] font-bold leading-tight px-1 truncate">
                  {room.label}
                </p>
                <div className="absolute top-1 right-1 w-4 h-4 bg-rose-500 rounded-full flex items-center justify-center">
                  <i className="ri-star-fill text-white" style={{ fontSize: 7 }}></i>
                </div>
              </button>
            ))}
          </div>
          {favoriteRoomObjects.length >= 2 && (
            <button
              onClick={() => onCompareRooms(favoriteRooms[0], favoriteRooms[1])}
              className="mt-2 w-full py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600 transition-all cursor-pointer flex items-center justify-center gap-1.5"
            >
              <i className="ri-layout-column-line"></i>
              Compare Top 2 Rooms Side-by-Side
            </button>
          )}
        </div>
      )}

      {favoriteRooms.length === 0 && (
        <div className={`px-4 pb-4 text-center`}>
          <div className={`py-4 rounded-xl border-2 border-dashed ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <i className={`ri-star-line text-2xl mb-1 block ${subtext}`}></i>
            <p className={`text-xs font-medium ${subtext}`}>Star rooms above to save them</p>
            <p className={`text-xs mt-0.5 ${subtext}`}>Then compare any two side-by-side</p>
          </div>
        </div>
      )}
    </div>
  );
}
